import type { RootState } from './store';
import { serializeQueryParams } from './utils';
import { toast } from 'sonner';

// Custom baseQuery that handles FormData properly
export const customBaseQuery = async (
  args: any,
  api: any,
  extraOptions: any
) => {
  const { getState } = api;
  const state = getState() as RootState;
  const token = state.auth.token;
  const userId = state.auth.userId;

  // Always use the proxy route, never direct backend URL
  if (!args.url) {
    console.error('❌ customBaseQuery called with undefined URL:', args);
    return { error: 'Invalid URL: args.url is undefined' };
  }
  let url = `/api/proxy${args.url}`;

  if (args.params) {
    const qs = serializeQueryParams(args.params as Record<string, unknown>);
    if (qs) url += `?${qs}`;
  }

  // console.log('Custom baseQuery called with:', args);
  // console.log('Final URL:', url);
  // console.log('URL parameters:', args.params);

  const headers: Record<string, string> = {
    language_iso: 'tr',
  };

  if (token) headers['Authorization'] = token;
  if (userId) headers['X-User-Id'] = userId;

  if (args.headers && typeof args.headers === 'object') {
    Object.assign(headers, args.headers as Record<string, string>);
  }

  // Prepare the body - stringify JSON objects for the proxy
  let body = args.body;
  const method = (args.method || 'GET').toUpperCase();

  // For FormData, don't set Content-Type - let browser handle it
  if (!(args.body instanceof FormData)) {
    if (args.body && typeof args.body === 'object') {
      body = JSON.stringify(args.body);
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }
    } else if (
      typeof args.body === 'string' &&
      args.body.length > 0 &&
      (method === 'POST' || method === 'PUT' || method === 'PATCH')
    ) {
      // RTK bazı mutation'larda body'yi önceden JSON.stringify ediyor; o zaman typeof 'object' olmaz
      if (!headers['Content-Type'] && !headers['content-type']) {
        headers['Content-Type'] = 'application/json';
      }
    }
  }

  try {
    // console.log('🚀 About to fetch:', url);
    const response = await fetch(url, {
      method,
      headers,
      body: body,
      cache: args.cache,
    });

    // console.log('📡 Response status:', response.status);
    // console.log(
    //   '📡 Response headers:',
    //   Object.fromEntries(response.headers.entries())
    // );

    // If a custom responseHandler is provided, use it
    if (args.responseHandler && typeof args.responseHandler === 'function') {
      const data = await args.responseHandler(response);
      return { data };
    }

    // Check content type to handle non-JSON responses
    const contentType = response.headers.get('content-type');

    // Handle PDF/blob responses
    if (
      contentType &&
      (contentType.includes('application/pdf') ||
        contentType.includes('application/octet-stream'))
    ) {
      const blob = await response.blob();
      return { data: blob };
    }

    const rawData = await response.json();
    const isWrappedResponse =
      rawData &&
      typeof rawData === 'object' &&
      'success' in rawData &&
      'str' in rawData &&
      'code' in rawData;

    // Normalize backend business errors (HTTP 200 but success=false)
    if (isWrappedResponse && rawData.success === false) {
      if (typeof window !== 'undefined') {
        toast.error(rawData.str || 'Request failed');
      }
      return {
        error: {
          status: response.status || 400,
          data: {
            ...rawData,
            message: rawData.str || 'Request failed',
          },
        },
      };
    }

    // Handle transport-level HTTP errors
    if (!response.ok) {
      const message =
        (rawData &&
          typeof rawData === 'object' &&
          ((rawData as any).str || (rawData as any).message)) ||
        response.statusText ||
        'İstek başarısız oldu';
      return {
        error: {
          status: response.status,
          data: {
            ...(rawData && typeof rawData === 'object' ? rawData : { rawData }),
            message,
          },
        },
      };
    }

    // console.log('📥 Raw backend response:', rawData);

    // For enum endpoints, return array directly without pagination wrapping
    if (args.url.includes('/enum') && Array.isArray(rawData)) {
      return { data: rawData };
    }

    // Handle plain array responses (e.g., entity-specific data_logs endpoints)
    if (Array.isArray(rawData)) {
      const transformedData = {
        data: rawData,
        total: rawData.length,
        skip: 0,
        take: rawData.length,
      };
      return { data: transformedData };
    }

    // Transform wrapped responses to expected PaginatedResponse format
    if (isWrappedResponse && rawData.success === true) {
      let parsedData;

      // Handle both string and already parsed data
      if (typeof rawData.data === 'string') {
        if (rawData.data === '') {
          parsedData = '';
        } else {
          try {
            parsedData = JSON.parse(rawData.data);
          } catch (e) {
            console.error('Failed to parse data string:', rawData.data);
            parsedData = rawData.data;
          }
        }
      } else {
        parsedData = rawData.data;
      }

      // For GET list requests, transform to PaginatedResponse
      if (Array.isArray(parsedData)) {
        const transformedData = {
          data: parsedData,
          total: rawData.size || parsedData.length,
          skip: 0, // Backend doesn't return this, use default
          take: parsedData.length, // Use actual data length
        };

        return { data: transformedData };
      } else if (
        parsedData === '' ||
        parsedData === null ||
        parsedData === undefined
      ) {
        // Keep wrapped response for success responses without payload
        return { data: rawData };
      } else {
        // For single item requests (GET by ID), return parsed object directly
        return { data: parsedData };
      }
    }

    // If the backend returned a plain object (not wrapped), return it directly
    if (
      (rawData && typeof rawData === 'object') ||
      typeof rawData === 'string' ||
      typeof rawData === 'number' ||
      typeof rawData === 'boolean'
    ) {
      return { data: rawData };
    } else {
      console.error('❌ Backend response error:', typeof rawData);
      console.error('❌ Backend response error:', rawData);
      return {
        error: {
          status: response.status,
          data: rawData,
        },
      };
    }
  } catch (error) {
    console.error('Custom baseQuery error:', error);
    return {
      error: {
        status: 0,
        data: error instanceof Error ? error.message : String(error),
      },
    };
  }
};
